var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, MenuItem, Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, } from '@mui/material';
import config from 'app/config';
import { prefixLoad, prefixUpdate } from 'app/store/retransmission-prefix';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getApiErrorMessage, PREFIX_NAME_MAX_LENGTH, validateDestinationUrl, validatePrefixName } from './prefixNameRules';
import { PREFIX_FIELD_HINTS } from './retransmissionPrefixHints';
import { buildPrefixPayload, isWialonD, normalizeType, toFlagBool } from './retransmissionPrefixPayload';
import RetransmissionSettingsDialog from './RetransmissionSettingsDialog';
import TransportGroupsAutocomplete from './TransportGroupsAutocomplete';
function HeadHint({ children, title }) {
    return (React.createElement(Tooltip, { title: title, placement: 'top' },
        React.createElement("span", { style: { cursor: 'help', borderBottom: '1px dotted currentColor' } }, children)));
}
const headCellSx = {
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    px: 0.75,
    py: 1,
};
const bodyCellSx = {
    verticalAlign: 'middle',
    px: 0.75,
    py: 1,
};
const dColumnCellSx = Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'center' });
const flagLabelSx = {
    mr: 0,
    ml: 0,
    my: 0,
    height: 24,
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    '& .MuiFormControlLabel-label': { ml: 0.25 },
    '& .MuiCheckbox-root': { py: 0, px: 0.25 },
};
function toDraft(row) {
    return {
        id: row.id,
        prefix: row.prefix || '',
        url: row.url || '',
        protocol: row.protocol,
        type: normalizeType(row.protocol, row.type),
        active: toFlagBool(row.active),
        send_sensors: toFlagBool(row.send_sensors),
        send_alarms: toFlagBool(row.send_alarms),
        send_guards: toFlagBool(row.send_guards),
        groups: [...(row.groups || [])],
    };
}
function groupIdsKey(groups) {
    return (groups || [])
        .map((g) => g.id)
        .sort((a, b) => a - b)
        .join(',');
}
function isRowDirty(server, draft) {
    if (!server || !draft)
        return false;
    return (draft.prefix.trim() !== (server.prefix || '') ||
        draft.url.trim() !== (server.url || '') ||
        draft.protocol !== server.protocol ||
        normalizeType(draft.protocol, draft.type) !== normalizeType(server.protocol, server.type) ||
        draft.active !== toFlagBool(server.active) ||
        draft.send_sensors !== toFlagBool(server.send_sensors) ||
        draft.send_alarms !== toFlagBool(server.send_alarms) ||
        draft.send_guards !== toFlagBool(server.send_guards) ||
        groupIdsKey(draft.groups) !== groupIdsKey(server.groups));
}
function describeDirtyFields(server, draft) {
    const lines = [];
    if (draft.prefix.trim() !== (server.prefix || '')) {
        lines.push(`префикс: «${server.prefix}» → «${draft.prefix.trim()}»`);
    }
    if (draft.url.trim() !== (server.url || '')) {
        lines.push(`куда: «${server.url}» → «${draft.url.trim()}»`);
    }
    if (draft.protocol !== server.protocol) {
        lines.push(`протокол: ${server.protocol} → ${draft.protocol}`);
    }
    const serverType = normalizeType(server.protocol, server.type);
    const draftType = normalizeType(draft.protocol, draft.type);
    if (serverType !== draftType) {
        lines.push(`тип: ${serverType || '—'} → ${draftType || '—'}`);
    }
    if (draft.active !== toFlagBool(server.active)) {
        lines.push(draft.active ? 'включение ретрансляции' : 'выключение ретрансляции');
    }
    if (draft.send_sensors !== toFlagBool(server.send_sensors) ||
        draft.send_alarms !== toFlagBool(server.send_alarms) ||
        draft.send_guards !== toFlagBool(server.send_guards)) {
        lines.push('флаги #D#');
    }
    if (groupIdsKey(draft.groups) !== groupIdsKey(server.groups)) {
        lines.push('транспортные группы');
    }
    return lines;
}
function RetransmissionSettings({ groupItems }) {
    var _a;
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [tableError, setTableError] = React.useState(null);
    const [drafts, setDrafts] = React.useState({});
    const [savingId, setSavingId] = React.useState(null);
    const [confirmId, setConfirmId] = React.useState(null);
    const dispatch = useDispatch();
    const session = useSelector((state) => state.session.session);
    const retransmissionPrefixState = useSelector((state) => state.retransmissionPrefix);
    const prefixes = (retransmissionPrefixState === null || retransmissionPrefixState === void 0 ? void 0 : retransmissionPrefixState.prefixes) || [];
    const isLoading = (retransmissionPrefixState === null || retransmissionPrefixState === void 0 ? void 0 : retransmissionPrefixState.isLoading) || false;
    const canManagePrefixes = (session === null || session === void 0 ? void 0 : session.role_id) === config.userRoles.SUPERADMIN || (session === null || session === void 0 ? void 0 : session.role_id) === config.userRoles.ADMIN;
    useEffect(() => {
        dispatch(prefixLoad());
    }, [dispatch]);
    useEffect(() => {
        setDrafts((prev) => {
            const next = {};
            for (const prefix of prefixes) {
                const existing = prev[prefix.id];
                if (existing && isRowDirty(prefix, existing)) {
                    next[prefix.id] = existing;
                }
                else {
                    next[prefix.id] = toDraft(prefix);
                }
            }
            return next;
        });
    }, [prefixes]);
    const handleOpenDialog = () => {
        setDialogOpen(true);
    };
    const handleCloseDialog = () => {
        setDialogOpen(false);
    };
    const patchDraft = (prefixId, patch) => {
        setTableError(null);
        setDrafts((prev) => {
            const current = prev[prefixId];
            if (!current)
                return prev;
            return Object.assign(Object.assign({}, prev), { [prefixId]: Object.assign(Object.assign({}, current), patch) });
        });
    };
    const handleFieldChange = (prefixId, field, value) => {
        const current = drafts[prefixId];
        if (!current)
            return;
        const patch = { [field]: value };
        if (field === 'protocol') {
            patch.type = value === 'WIALON' ? 'SD' : null;
            patch.send_sensors = false;
            patch.send_alarms = false;
            patch.send_guards = false;
        }
        if (field === 'type') {
            patch.type = normalizeType(current.protocol, value);
            if (!isWialonD(current.protocol, patch.type)) {
                patch.send_sensors = false;
                patch.send_alarms = false;
                patch.send_guards = false;
            }
        }
        patchDraft(prefixId, patch);
    };
    const handleCancelRow = (prefixId) => {
        const server = prefixes.find((p) => p.id === prefixId);
        if (!server)
            return;
        setTableError(null);
        setDrafts((prev) => (Object.assign(Object.assign({}, prev), { [prefixId]: toDraft(server) })));
    };
    const handleRequestSave = (prefixId) => {
        const server = prefixes.find((p) => p.id === prefixId);
        const draft = drafts[prefixId];
        if (!server || !draft || !isRowDirty(server, draft))
            return;
        const prefixError = validatePrefixName(draft.prefix);
        if (prefixError) {
            setTableError(prefixError);
            return;
        }
        const urlError = validateDestinationUrl(draft.url);
        if (urlError) {
            setTableError(urlError);
            return;
        }
        setTableError(null);
        setConfirmId(prefixId);
    };
    const handleConfirmSave = () => __awaiter(this, void 0, void 0, function* () {
        const prefixId = confirmId;
        const server = prefixes.find((p) => p.id === prefixId);
        const draft = drafts[prefixId];
        if (!server || !draft) {
            setConfirmId(null);
            return;
        }
        setSavingId(prefixId);
        setTableError(null);
        const payloadDraft = Object.assign(Object.assign({}, draft), { prefix: draft.prefix.trim(), url: draft.url.trim() });
        const result = yield dispatch(prefixUpdate(prefixId, buildPrefixPayload(payloadDraft)));
        setSavingId(null);
        setConfirmId(null);
        if (!(result === null || result === void 0 ? void 0 : result.success)) {
            setTableError(getApiErrorMessage(result === null || result === void 0 ? void 0 : result.error, 'Не удалось сохранить префикс'));
            return;
        }
        setDrafts((prev) => (Object.assign(Object.assign({}, prev), { [prefixId]: toDraft(result.data || payloadDraft) })));
    });
    const handlePrefixCreated = () => {
        dispatch(prefixLoad());
    };
    if (isLoading) {
        return (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' } },
            React.createElement(CircularProgress, null)));
    }
    const confirmServer = confirmId != null ? prefixes.find((p) => p.id === confirmId) : null;
    const confirmDraft = confirmId != null ? drafts[confirmId] : null;
    const confirmChanges = confirmServer && confirmDraft ? describeDirtyFields(confirmServer, confirmDraft) : [];
    const header = (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 } },
        React.createElement(Typography, { variant: 'body1', color: 'text.secondary' }, "\u0421\u043F\u0438\u0441\u043E\u043A \u043F\u0440\u0435\u0444\u0438\u043A\u0441\u043E\u0432 \u0440\u0435\u0442\u0440\u0430\u043D\u0441\u043B\u044F\u0446\u0438\u0438. \u041F\u0440\u0430\u0432\u043A\u0438 \u0432\u0441\u0442\u0443\u043F\u0430\u044E\u0442 \u0432 \u0441\u0438\u043B\u0443 \u043F\u043E\u0441\u043B\u0435 \u00AB\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C\u00BB."),
        canManagePrefixes && (React.createElement(Button, { variant: 'contained', startIcon: React.createElement(AddIcon, null), onClick: handleOpenDialog, sx: {
                textTransform: 'none',
                borderRadius: 1,
                px: 3,
                py: 1,
            } }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043F\u0440\u0435\u0444\u0438\u043A\u0441"))));
    if (!Array.isArray(prefixes) || prefixes.length === 0) {
        return (React.createElement(Box, null,
            header,
            React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' } },
                React.createElement(Typography, { variant: 'body2', color: 'text.secondary' }, "\u041D\u0435\u0442 \u043F\u0440\u0435\u0444\u0438\u043A\u0441\u043E\u0432 \u0440\u0435\u0442\u0440\u0430\u043D\u0441\u043B\u044F\u0446\u0438\u0438")),
            canManagePrefixes && (React.createElement(RetransmissionSettingsDialog, { open: dialogOpen, onClose: handleCloseDialog, onCreated: handlePrefixCreated, groupItems: groupItems }))));
    }
    return (React.createElement(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            gap: 0,
        } },
        header,
        tableError && (React.createElement(Alert, { severity: 'error', onClose: () => setTableError(null), sx: { mb: 2 } }, tableError)),
        React.createElement(TableContainer, { sx: {
                flex: 1,
                minHeight: 0,
                maxHeight: 'calc(100vh - 260px)',
                overflow: 'auto',
            } },
            React.createElement(Table, { stickyHeader: true, size: 'small', sx: { tableLayout: 'fixed', width: '100%', borderCollapse: 'separate' } },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 48, textAlign: 'center' }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.active }, "\u0412\u043A\u043B.")),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 88 }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.prefix }, "\u041F\u0440\u0435\u0444\u0438\u043A\u0441")),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: '22%' }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.url }, "\u041A\u0443\u0434\u0430")),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 108 }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.protocol }, "\u041F\u0440\u043E\u0442\u043E\u043A\u043E\u043B")),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 72 }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.type }, "\u0422\u0438\u043F")),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 100, textAlign: 'center' }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.dFlags }, "#D#")),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 'auto' }) },
                            React.createElement(HeadHint, { title: PREFIX_FIELD_HINTS.groups }, "\u0422\u0440\u0430\u043D\u0441\u043F\u043E\u0440\u0442\u043D\u044B\u0435 \u0433\u0440\u0443\u043F\u043F\u044B")),
                        canManagePrefixes && React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 118 }) }, "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044F"))),
                React.createElement(TableBody, null, prefixes.map((serverRow) => {
                    const draft = drafts[serverRow.id] || toDraft(serverRow);
                    const dirty = isRowDirty(serverRow, draft);
                    const isTypeDisabled = draft.protocol !== 'WIALON';
                    const wialonD = isWialonD(draft.protocol, draft.type);
                    const isActive = draft.active;
                    const isSaving = savingId === serverRow.id;
                    return (React.createElement(TableRow, { key: serverRow.id, hover: true, sx: Object.assign({ 
                            // Без opacity на строке — иначе бледнеет «Сохранить» при выключенном префиксе.
                            bgcolor: dirty ? 'action.selected' : isActive ? 'transparent' : 'action.hover' }, (!isActive && !dirty
                            ? {
                                '& .MuiOutlinedInput-root, & .MuiSelect-select, & .MuiChip-root, & .MuiTypography-caption': {
                                    opacity: 0.72,
                                },
                            }
                            : {})) },
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'center' }) },
                            React.createElement(Tooltip, { title: isActive ? 'Отключить ретрансляцию по префиксу' : 'Включить ретрансляцию по префиксу' },
                                React.createElement(Switch, { checked: isActive, onChange: (e) => handleFieldChange(serverRow.id, 'active', e.target.checked), disabled: !canManagePrefixes || isSaving, size: 'small', inputProps: { 'aria-label': 'Активность префикса' } }))),
                        React.createElement(TableCell, { sx: bodyCellSx },
                            React.createElement(TextField, { value: draft.prefix, onChange: (e) => handleFieldChange(serverRow.id, 'prefix', e.target.value), variant: 'outlined', size: 'small', fullWidth: true, inputProps: { maxLength: PREFIX_NAME_MAX_LENGTH }, disabled: !canManagePrefixes || isSaving, sx: {
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: canManagePrefixes ? 'white' : '#f8f9fa',
                                    },
                                } })),
                        React.createElement(TableCell, { sx: bodyCellSx },
                            React.createElement(TextField, { value: draft.url, onChange: (e) => handleFieldChange(serverRow.id, 'url', e.target.value), variant: 'outlined', size: 'small', fullWidth: true, inputProps: { maxLength: 500 }, disabled: !canManagePrefixes || isSaving, placeholder: draft.protocol === 'WIALON' ? 'host:20332' : 'https://host/path или host:port', sx: {
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: canManagePrefixes ? 'white' : '#f8f9fa',
                                    },
                                } })),
                        React.createElement(TableCell, { sx: bodyCellSx },
                            React.createElement(FormControl, { size: 'small', fullWidth: true },
                                React.createElement(Select, { value: draft.protocol, onChange: (e) => handleFieldChange(serverRow.id, 'protocol', e.target.value), disabled: !canManagePrefixes || isSaving },
                                    React.createElement(MenuItem, { value: 'XML' }, "XML"),
                                    React.createElement(MenuItem, { value: 'WIALON' }, "WIALON"),
                                    React.createElement(MenuItem, { value: 'EGTS' }, "EGTS"),
                                    React.createElement(MenuItem, { value: 'POST' }, "POST")))),
                        React.createElement(TableCell, { sx: bodyCellSx },
                            React.createElement(FormControl, { size: 'small', fullWidth: true },
                                React.createElement(Select, { value: draft.protocol === 'WIALON' ? draft.type || 'SD' : '', onChange: (e) => handleFieldChange(serverRow.id, 'type', e.target.value), disabled: isTypeDisabled || !canManagePrefixes || isSaving, displayEmpty: true, renderValue: (selected) => {
                                        if (draft.protocol !== 'WIALON') {
                                            return '—';
                                        }
                                        return selected === 'D' ? 'D' : 'SD';
                                    }, sx: {
                                        '& .MuiSelect-select.Mui-disabled': {
                                            backgroundColor: '#f5f5f5',
                                        },
                                    } },
                                    React.createElement(MenuItem, { value: 'SD' }, "SD"),
                                    React.createElement(MenuItem, { value: 'D' }, "D")))),
                        React.createElement(TableCell, { sx: dColumnCellSx }, wialonD ? (React.createElement(Box, { sx: {
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                mx: 'auto',
                            } },
                            React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { size: 'small', checked: draft.send_sensors, onChange: (e) => handleFieldChange(serverRow.id, 'send_sensors', e.target.checked), disabled: !canManagePrefixes || isSaving }), label: React.createElement(Tooltip, { title: '\u0414\u0430\u0442\u0447\u0438\u043A\u0438 \u0414\u0423\u0422/\u0414\u0423\u041D (dtN, duN, duk)' },
                                    React.createElement(Typography, { variant: 'caption' }, "\u0414\u0423\u0422/\u0414\u0423\u041D")), sx: flagLabelSx }),
                            React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { size: 'small', checked: draft.send_guards, onChange: (e) => handleFieldChange(serverRow.id, 'send_guards', e.target.checked), disabled: !canManagePrefixes || isSaving }), label: React.createElement(Tooltip, { title: '\u041E\u0445\u0440\u0430\u043D\u0430 (grd)' },
                                    React.createElement(Typography, { variant: 'caption' }, "\u041E\u0445\u0440\u0430\u043D\u0430")), sx: flagLabelSx }),
                            React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { size: 'small', checked: draft.send_alarms, onChange: (e) => handleFieldChange(serverRow.id, 'send_alarms', e.target.checked), disabled: !canManagePrefixes || isSaving }), label: React.createElement(Tooltip, { title: '\u0422\u0440\u0435\u0432\u043E\u0433\u0430 (alrm)' },
                                    React.createElement(Typography, { variant: 'caption' }, "\u0422\u0440\u0435\u0432\u043E\u0433\u0430")), sx: flagLabelSx }))) : (React.createElement(Typography, { variant: 'caption', color: 'text.secondary', component: 'span' }, "\u2014"))),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { overflow: 'hidden' }) },
                            React.createElement(TransportGroupsAutocomplete, { options: groupItems || [], value: draft.groups || [], onChange: (groups) => handleFieldChange(serverRow.id, 'groups', groups), disabled: !canManagePrefixes || isSaving, placeholder: '\u041F\u043E\u0438\u0441\u043A \u0433\u0440\u0443\u043F\u043F\u044B\u2026', size: 'small', limitTags: 1 })),
                        canManagePrefixes && (React.createElement(TableCell, { sx: bodyCellSx },
                            React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.25 } },
                                React.createElement(LoadingButton, { variant: 'contained', size: 'small', disabled: !dirty, loading: isSaving, onClick: () => handleRequestSave(serverRow.id), sx: { textTransform: 'none', minWidth: 0, px: 1.25 } }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C"),
                                React.createElement(Tooltip, { title: '\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F' },
                                    React.createElement("span", null,
                                        React.createElement(IconButton, { size: 'small', disabled: !dirty || isSaving, onClick: () => handleCancelRow(serverRow.id), "aria-label": '\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F' },
                                            React.createElement(UndoIcon, { fontSize: 'small' })))))))));
                })))),
        React.createElement(Dialog, { open: confirmId != null, onClose: () => !savingId && setConfirmId(null), maxWidth: 'sm', fullWidth: true },
            React.createElement(DialogTitle, null, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F \u0440\u0435\u0442\u0440\u0430\u043D\u0441\u043B\u044F\u0446\u0438\u0438?"),
            React.createElement(DialogContent, null,
                React.createElement(Typography, { variant: 'body2', sx: { mb: 1 } },
                    "\u041F\u0440\u0435\u0444\u0438\u043A\u0441 \u00AB",
                    ((_a = confirmDraft === null || confirmDraft === void 0 ? void 0 : confirmDraft.prefix) === null || _a === void 0 ? void 0 : _a.trim()) || (confirmServer === null || confirmServer === void 0 ? void 0 : confirmServer.prefix),
                    "\u00BB \u0431\u0443\u0434\u0435\u0442 \u043E\u0431\u043D\u043E\u0432\u043B\u0451\u043D \u043D\u0430 \u0441\u0435\u0440\u0432\u0435\u0440\u0435."),
                confirmChanges.length > 0 && (React.createElement(Box, { component: 'ul', sx: { m: 0, pl: 2 } }, confirmChanges.map((line) => (React.createElement(Typography, { key: line, component: 'li', variant: 'body2', color: 'text.secondary' }, line)))))),
            React.createElement(DialogActions, { sx: { px: 3, pb: 2 } },
                React.createElement(Button, { onClick: () => setConfirmId(null), disabled: Boolean(savingId), sx: { textTransform: 'none' } }, "\u041E\u0442\u043C\u0435\u043D\u0430"),
                React.createElement(LoadingButton, { variant: 'contained', loading: Boolean(savingId), onClick: handleConfirmSave, sx: { textTransform: 'none' } }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C"))),
        canManagePrefixes && (React.createElement(RetransmissionSettingsDialog, { open: dialogOpen, onCreated: handlePrefixCreated, onClose: handleCloseDialog, groupItems: groupItems }))));
}
export default RetransmissionSettings;
